import { useTranslation } from 'react-i18next'
import { format, parseISO, isValid } from 'date-fns'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog'
import type { TrainingPlan } from '../../types/training-plan'

interface PlanDetailsDialogProps {
  plan: TrainingPlan
  open: boolean
  onOpenChange: (open: boolean) => void
}

/**
 * Read-only summary of the plan questionnaire: every question and its answer,
 * shown as scrollable text. Available to both the athlete and the coach.
 */
export function PlanDetailsDialog({ plan, open, onOpenChange }: PlanDetailsDialogProps) {
  const { t } = useTranslation()

  const formatDate = (value: string | null): string | null => {
    if (!value) return null
    const parsed = parseISO(value)
    return isValid(parsed) ? format(parsed, 'PP') : value
  }

  const days = plan.preferred_session_days.map((d) => t(`trainingPlans.days.${d}`)).join(', ')

  const entries: Array<{ label: string; value: string | null }> = [
    { label: t('trainingPlans.wizard.fields.discipline'), value: plan.discipline ? t(`trainingPlans.discipline.${plan.discipline}`) : null },
    { label: t('trainingPlans.wizard.fields.experience'), value: plan.training_experience ? t(`trainingPlans.experience.${plan.training_experience}`) : null },
    { label: t('trainingPlans.wizard.fields.startDate'), value: formatDate(plan.start_date) },
    { label: t('trainingPlans.wizard.fields.lengthWeeks'), value: plan.length_weeks != null ? String(plan.length_weeks) : null },
    { label: t('trainingPlans.wizard.fields.sessionDuration'), value: plan.session_duration_minutes != null ? String(plan.session_duration_minutes) : null },
    { label: t('trainingPlans.wizard.fields.sessionsPerWeek'), value: plan.sessions_per_week != null ? String(plan.sessions_per_week) : null },
    { label: t('trainingPlans.wizard.fields.preferredDays'), value: days || null },
    { label: t('trainingPlans.wizard.fields.goal'), value: plan.goal },
    { label: t('trainingPlans.wizard.fields.typicalGrade'), value: plan.typical_grade },
    { label: t('trainingPlans.wizard.fields.bestGrade'), value: plan.best_grade },
    { label: t('trainingPlans.wizard.fields.styleStrengths'), value: plan.style_strengths },
    { label: t('trainingPlans.wizard.fields.styleLimiters'), value: plan.style_limiters },
    { label: t('trainingPlans.wizard.fields.recentTraining'), value: plan.recent_training },
    { label: t('trainingPlans.wizard.fields.facilities'), value: plan.facilities },
    { label: t('trainingPlans.wizard.fields.workDemands'), value: plan.work_demands },
    { label: t('trainingPlans.wizard.fields.otherActivity'), value: plan.other_activity },
    { label: t('trainingPlans.wizard.fields.injuriesConstraints'), value: plan.injuries_constraints },
    { label: t('trainingPlans.wizard.fields.preferences'), value: plan.preferences },
  ]

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>{t('trainingPlans.builder.detailsTitle')}</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-4 overflow-y-auto -mx-1 px-1 pb-2">
          {entries.map((entry) => (
            <div key={entry.label} className="flex flex-col gap-0.5">
              <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {entry.label}
              </span>
              <span className="text-sm whitespace-pre-wrap break-words">
                {entry.value && entry.value.trim() !== '' ? entry.value : '—'}
              </span>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  )
}
